/**
 * HOL Creative Solutions, archived blog content.
 * These posts predate the Feb 2026 repositioning.
 * Kept live for old inbound URLs. Not in nav. Surfaced on /case-studies as field notes.
 */

export const POSTS = [
  {
    slug: "how-landlords-in-central-indiana-can-streamline-property-management",
    title:
      "How Landlords in Central Indiana Can Streamline Property Management With Operational Support",
    date: "2026-05-05",
    category: "Landlords",
    excerpt:
      "Self-managing landlords in Central Indiana hit the same operational walls. Vendor chaos. Inconsistent follow-up. No documented processes. HOL builds the operational support layer that helps landlords run tighter.",
    body: [
      {
        type: "p",
        text: "Managing rental properties in Central Indiana is manageable when you have one or two units.",
      },
      {
        type: "p",
        text: "Add a few more and the cracks start to show. Vendors that do not show up on time. Turnovers that drag. Maintenance requests that fall through the cracks. No clear process for any of it.",
      },
      {
        type: "p",
        text: "Sound familiar?",
      },
      {
        type: "p",
        text: "Most landlords do not need a full property management company. They need operational support. The systems, vendor relationships, and coordination infrastructure that turn a chaotic rental portfolio into a predictable operation.",
      },
      {
        type: "h2",
        text: "The Operational Gaps Most Landlords Do Not Talk About",
      },
      {
        type: "p",
        text: "Self-managing landlords across Columbus, Indianapolis, Lafayette, Bloomington, and surrounding Central Indiana markets run into the same gaps:",
      },
      {
        type: "list",
        items: [
          "No reliable vendor network. Finding a cleanout crew, floor prep team, or debris hauler for every turnover means starting from scratch each time.",
          "No intake or CRM system. Tenant inquiries, maintenance requests, and vendor communications all live in text threads and email inboxes.",
          "No documented turnover process. Every vacancy feels like a new project instead of a repeatable system.",
          "No procurement leverage. Buying supplies one job at a time means overpaying consistently.",
          "No delegation infrastructure. Because nothing is documented, the landlord has to be personally involved in every decision.",
        ],
      },
      {
        type: "h2",
        text: "What Operational Support Actually Looks Like for Landlords",
      },
      {
        type: "p",
        text: "Operational support is not property management.",
      },
      {
        type: "p",
        text: "A property management company takes over the landlord relationship entirely. Tenant screening. Rent collection. Maintenance coordination. Legal compliance. That is a full handoff.",
      },
      {
        type: "p",
        text: "Operational support is different. It builds the systems and infrastructure behind the landlord's own operation. The landlord stays in control. The operational burden gets lighter.",
      },
      {
        type: "p",
        text: "Here is what that looks like in practice:",
      },
      {
        type: "list",
        items: [
          "Vendor coordination. A reliable network of affiliated property service partners for turnovers, cleanouts, floor prep, and debris removal across Central Indiana.",
          "CRM and intake workflows. Organized systems for tracking vacancies, vendor communications, and follow-up so nothing gets lost.",
          "Turnover SOPs. Documented step-by-step processes for vacancy preparation. Every turnover runs the same way every time.",
          "Procurement coordination. Sourcing materials through organized vendor relationships rather than one-off purchases.",
          "Business development support. For landlords who want to grow their portfolio or launch affiliated property service operations.",
        ],
      },
      {
        type: "h2",
        text: "How HOL Creative Solutions Supports Landlords in Central Indiana",
      },
      {
        type: "p",
        text: "HOL Creative Solutions is an operations hub and venture support company based in Central Indiana.",
      },
      {
        type: "p",
        text: "HOL works with landlords who want to self-manage more effectively. The work is building the operational infrastructure behind their rental activity.",
      },
      {
        type: "p",
        text: "Rather than taking over the landlord relationship, HOL organizes the systems, vendor coordination, and documentation that make the operation run more professionally.",
      },
      {
        type: "p",
        text: "Affiliated property service ventures in HOL's network can support turnover-related services. Cleanouts. Junk removal. Floor prep. Property readiness. Across Columbus, Indianapolis, Lafayette, Bloomington, Greenwood, Franklin, Seymour, and surrounding areas.",
      },
      {
        type: "h2",
        text: "The Difference Between Struggling and Scaling",
      },
      {
        type: "p",
        text: "Landlords who stay stuck at two or three units are usually stuck because of operational friction. Not because of deal flow or capital.",
      },
      {
        type: "p",
        text: "Every vacancy takes too long. Every turnover is a scramble. Every vendor relationship has to be rebuilt from scratch.",
      },
      {
        type: "p",
        text: "Landlords who scale past that point have solved the operational layer first.",
      },
      {
        type: "p",
        text: "They have a vendor they call for every cleanout. They have a process for every turnover. They have a CRM that tells them where every unit stands.",
      },
      {
        type: "p",
        text: "The deals come after the systems.",
      },
      {
        type: "h2",
        text: "Who This Is For",
      },
      {
        type: "list",
        items: [
          "Landlords managing 2 to 10 units in Central Indiana who want to self-manage more professionally.",
          "Landlords experiencing high turnover friction. Cleanouts that take too long. Vacancies that drag. Vendors that are unreliable.",
          "Landlords who want to scale their portfolio but feel operationally maxed out at their current size.",
          "Landlords considering launching an affiliated property service operation to support their own rental activity.",
        ],
      },
      {
        type: "h2",
        text: "Let's Talk About Your Operation",
      },
      {
        type: "p",
        text: "If you are self-managing rental properties in Central Indiana and the operational side is slowing you down, HOL can help.",
      },
      {
        type: "p",
        text: "Reach out at holcreativesolutions@gmail.com or call (708) 658-4272 to start the conversation.",
      },
    ],
  },
  {
    slug: "what-does-hol-creative-solutions-do-for-real-estate-investors",
    title: "What Does HOL Creative Solutions Do for Real Estate Investors?",
    date: "2026-05-05",
    category: "Real Estate",
    excerpt:
      "HOL Creative Solutions is not a single-service provider. It is the operational backbone behind real estate investors who need systems, vendor coordination, procurement support, and CRM infrastructure to move faster.",
    body: [
      {
        type: "p",
        text: "Real estate investors move fast.",
      },
      {
        type: "p",
        text: "Wholesalers, flippers, landlords, and acquisition-focused operators do not have time to build operational infrastructure from scratch every time a deal comes through.",
      },
      {
        type: "p",
        text: "That is the gap HOL Creative Solutions was built to fill.",
      },
      {
        type: "h2",
        text: "HOL Is Not a Single-Service Provider",
      },
      {
        type: "p",
        text: "Most vendors in the property space offer one thing. A cleanout crew. A contractor. A title company.",
      },
      {
        type: "p",
        text: "HOL operates differently. HOL Creative Solutions is an operations hub and venture support company.",
      },
      {
        type: "p",
        text: "That means HOL builds and manages the systems, workflows, vendor relationships, and coordination infrastructure that real estate investors need to operate professionally. Without having to build it themselves.",
      },
      {
        type: "p",
        text: "Think of HOL as the operational backbone behind your investment activity. Whether you are running five deals a year or building toward a portfolio, HOL helps organize the moving parts so you can focus on acquisitions and closings.",
      },
      {
        type: "h2",
        text: "The Real Pain Points Real Estate Investors Face",
      },
      {
        type: "p",
        text: "After working with investors, wholesalers, and property operators across Central Indiana, HOL has identified the operational gaps that consistently slow deals down and erode margins:",
      },
      {
        type: "list",
        items: [
          "No reliable vendor network for property readiness. Cleanouts. Floor prep. Debris removal. Deals sit longer than they should.",
          "No CRM or intake system to track leads, follow up consistently, or hand off to partners without losing context.",
          "No procurement process for sourcing materials at scale. Overpaying on supplies across multiple projects.",
          "No documented SOPs. Every deal feels like starting from scratch. Delegation becomes nearly impossible.",
          "No operational structure for affiliated service ventures. The investor is also managing labor, scheduling, and logistics personally.",
        ],
      },
      {
        type: "h2",
        text: "How HOL Supports Real Estate Investors Specifically",
      },
      {
        type: "p",
        text: "HOL's support spans several operational lanes depending on what the investor needs most:",
      },
      {
        type: "list",
        items: [
          "CRM and intake workflow setup. Every lead, deal, and vendor interaction is tracked, followed up, and never lost.",
          "Vendor and procurement coordination. HOL manages relationships with affiliated service ventures that handle property readiness, so investors have a reliable network to call on.",
          "Acquisition and disposition support. Strategic coordination for buy-side and sell-side decisions in Central Indiana markets.",
          "Operational SOPs. Documented processes for recurring deal activities so teams can execute consistently. The investor does not have to be in every conversation.",
          "Property service venture support. For investors who want to launch or support an affiliated cleanout or property readiness operation, HOL builds the systems behind it.",
        ],
      },
      {
        type: "h2",
        text: "Why the Operations Hub Model Works for Investors",
      },
      {
        type: "p",
        text: "The traditional model puts the investor at the center of every operational decision. Vendor calls. Scheduling. Procurement. Follow-up.",
      },
      {
        type: "p",
        text: "That model does not scale.",
      },
      {
        type: "p",
        text: "The operations hub model puts systems and coordination infrastructure at the center. The investor reviews outcomes rather than managing inputs.",
      },
      {
        type: "p",
        text: "HOL sits between the investor and the execution layer. Organizing vendor relationships. Building intake systems. Managing procurement. Documenting processes.",
      },
      {
        type: "p",
        text: "The investor's time stays focused on deal flow. Not day-to-day operational noise.",
      },
      {
        type: "h2",
        text: "Serving Central Indiana and Beyond",
      },
      {
        type: "p",
        text: "HOL Creative Solutions operates across Central Indiana. Columbus. Indianapolis. Lafayette. Bloomington. Greenwood. Franklin. Seymour. And surrounding markets.",
      },
      {
        type: "p",
        text: "If you are an investor actively working deals in Central Indiana and need operational infrastructure behind your activity, HOL is built for that conversation.",
      },
      {
        type: "h2",
        text: "Ready to Talk About What HOL Can Support?",
      },
      {
        type: "p",
        text: "Whether you need CRM setup, vendor coordination, procurement support, or operational structure for a property service venture, HOL helps organize the path forward.",
      },
      {
        type: "p",
        text: "Reach out at holcreativesolutions@gmail.com or call (708) 658-4272 to start the conversation.",
      },
    ],
  },
  {
    slug: "what-is-a-property-service-venture",
    title: "Operations Infrastructure for Property Service Ventures",
    date: "2026-05-05",
    category: "Venture Support",
    excerpt:
      "Property service operators run on execution instincts. HOL builds the operations infrastructure behind them. CRM. Lead capture. Vendor coordination. SOPs. The systems that turn execution into a business.",
    body: [
      {
        type: "p",
        text: "Property service operators know how to do the work.",
      },
      {
        type: "p",
        text: "They clear properties. Remove junk. Prep floors. Recover donation-worthy items.",
      },
      {
        type: "p",
        text: "What they typically do not have is the operations infrastructure behind the work.",
      },
      {
        type: "p",
        text: "That is what HOL Creative Solutions builds.",
      },
      {
        type: "h2",
        text: "Who We Build Operations For",
      },
      {
        type: "p",
        text: "HOL supports the operations layer behind property service businesses. The category includes:",
      },
      {
        type: "list",
        items: [
          "Junk removal and property cleanout operations.",
          "Carpet removal and floor preparation services.",
          "Debris hauling and post-renovation cleanup.",
          "Estate clearance and donation-conscious item recovery.",
          "Vacancy prep and turnover services for landlords and property managers.",
        ],
      },
      {
        type: "p",
        text: "These businesses serve homeowners, landlords, real estate investors, wholesalers, agents, and property managers. Anyone who needs a property cleared, prepped, or made ready for its next chapter.",
      },
      {
        type: "h2",
        text: "Where Property Service Operators Lose Revenue",
      },
      {
        type: "p",
        text: "After working with property service operators, the same operational gaps show up over and over:",
      },
      {
        type: "list",
        items: [
          "No lead capture system. Inquiries come in through text and social media with no organized intake process.",
          "No CRM. Follow-up is inconsistent. Quotes get lost. Potential jobs fall through.",
          "No documented SOPs. Every job relies on the owner's personal knowledge. Delegation becomes nearly impossible.",
          "No vendor or procurement relationships. Materials and disposal costs are unmanaged and unpredictable.",
          "No professional web presence or funnel. The business looks informal even when the work is excellent.",
        ],
      },
      {
        type: "p",
        text: "None of these problems are about doing the job. They are about everything around the job.",
      },
      {
        type: "h2",
        text: "What HOL Builds for Property Service Operators",
      },
      {
        type: "p",
        text: "HOL Creative Solutions is an operations infrastructure partner. We do not perform physical services. We build and manage the systems behind operators who do.",
      },
      {
        type: "p",
        text: "Here is what that looks like in practice:",
      },
      {
        type: "list",
        items: [
          "Lead capture systems. Website funnels, contact forms, and intake configured to capture and qualify inquiries automatically.",
          "CRM and intake workflows. Pipeline setup, deal stages, custom properties, and follow-up sequences so no lead is ever lost.",
          "Website and funnel support. Professional web presence built around the operator's services, service areas, and target clients.",
          "Vendor and procurement coordination. Relationships and sourcing infrastructure for disposal, materials, and subcontractor needs.",
          "Operational SOPs and documentation. Step-by-step process documentation for intake, quoting, scheduling, execution, and follow-up.",
          "Business development support. Strategic guidance for growing the client base, referral network, and revenue.",
        ],
      },
      {
        type: "h2",
        text: "The Model: Operations Layer Separate From Execution Layer",
      },
      {
        type: "p",
        text: "HOL separates the operations infrastructure layer from the physical execution layer.",
      },
      {
        type: "p",
        text: "HOL manages systems, coordination, and business development.",
      },
      {
        type: "p",
        text: "The operator manages crews, scheduling, and field execution.",
      },
      {
        type: "p",
        text: "This structure lets property service businesses operate professionally from day one. The owner does not have to build every system while also running jobs.",
      },
      {
        type: "p",
        text: "It also creates a foundation for operators who want a mission-driven component. Donation-conscious item recovery that diverts usable goods from landfills and connects them with families and nonprofits in need.",
      },
      {
        type: "h2",
        text: "Who This Is For",
      },
      {
        type: "p",
        text: "HOL's property service venture support is designed for:",
      },
      {
        type: "list",
        items: [
          "Operators running a cleanout or junk removal business who need systems behind the work.",
          "Existing property service operators who need structure to scale beyond their current capacity.",
          "Real estate investors who run an affiliated property service operation to support their acquisition and disposition activity.",
          "Mission-driven operators who want to combine property service work with donation recovery and community impact.",
        ],
      },
      {
        type: "h2",
        text: "Based in Central Indiana",
      },
      {
        type: "p",
        text: "HOL Creative Solutions supports property service operators across Central Indiana. Columbus. Indianapolis. Lafayette. Bloomington. Greenwood. Franklin. Seymour. And surrounding areas.",
      },
      {
        type: "p",
        text: "If you are running or scaling a property service business in this region, HOL is the operations partner built for that work.",
      },
      {
        type: "h2",
        text: "Let's Talk About Your Operation",
      },
      {
        type: "p",
        text: "Whether you are scaling an existing operation or starting to formalize one, HOL can help you build the systems behind the work.",
      },
      {
        type: "p",
        text: "Reach out at holcreativesolutions@gmail.com or call (708) 658-4272 to start the conversation.",
      },
    ],
  },
];

export function getPostBySlug(slug) {
  return POSTS.find((p) => p.slug === slug) || null;
}

export function formatPostDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}
